import { IT_VERBS, itPresent } from './italian';
import { compound, type LanguageTenses, type Tense } from './tenses';

// ─────────────────────────────────────────────────────────────────────────────
// Italian: every tense beyond the presente (its own topic). Each tense lists
// only verbs regular in it:
//   • compound tenses need a regular participle (-ato/-uto/-ito), so prendere
//     (preso), leggere (letto), scrivere (scritto), vedere (visto), chiedere,
//     correre, mettere, aprire (aperto) and offrire (offerto) are left out
//   • passato remoto: -ere verbs are mostly irregular (presi, lessi…), so only
//     credere / vendere / ricevere / ripetere, accepting both -ei and -etti
//   • futuro / condizionale: vedere is irregular (vedrò), so it's left out
// Spelling kept: cercherò, pagherei, mangerò, studierai; congiuntivo cerchi,
// mangino (no double i).
// Essere verbs (partire, arrivare, tornare, entrare, restare) accept every
// agreement: partito / partita / partiti / partite.
// ─────────────────────────────────────────────────────────────────────────────

const EXTRA: Record<string, string> = {
  arrivare: 'to arrive', tornare: 'to return', entrare: 'to enter', restare: 'to stay', ripetere: 'to repeat',
};
const ALL: Record<string, string> = { ...IT_VERBS, ...EXTRA };
const ESSERE = new Set(['partire', 'arrivare', 'tornare', 'entrare', 'restare']);
const IRREG_PP = new Set(['prendere', 'leggere', 'scrivere', 'vedere', 'chiedere', 'correre', 'mettere', 'aprire', 'offrire']);

const pick = (keep: (v: string) => boolean) => Object.fromEntries(Object.entries(ALL).filter(([v]) => keep(v)));
const REG_PP = pick(v => !IRREG_PP.has(v));
const NOT_VEDERE = pick(v => v !== 'vedere');
const REMOTO = pick(v => v.endsWith('are') || v.endsWith('ire') || ['credere', 'vendere', 'ricevere', 'ripetere'].includes(v));

type T = 'are' | 'ere' | 'ire';
const type = (v: string) => v.slice(-3) as T;
const root = (v: string) => v.slice(0, -3);

/** -are future/conditional stem: parler-, cercher-, manger-, studier-. */
function futStem(v: string): string {
  const t = type(v);
  if (t === 'ire') return root(v) + 'ir';
  if (t === 'ere') return root(v) + 'er';
  let r = root(v);
  if (/[cg]$/.test(r)) r += 'h';
  else if (/(ci|gi)$/.test(r)) r = r.slice(0, -1);      // mangiare → manger-, cominciare → comincer-
  return r + 'er';
}

const PP_END = { are: 'ato', ere: 'uto', ire: 'ito' };
const participle = (v: string) => root(v) + PP_END[type(v)];
const PLURAL = [false, false, false, true, true, true];
function agreeing(v: string, i: number): string[] {
  const pp = participle(v);
  if (!ESSERE.has(v)) return [pp];
  const s = pp.slice(0, -1);
  return PLURAL[i] ? [s + 'i', s + 'e'] : [s + 'o', s + 'a'];
}

const AUX = {
  pres: { avere: ['ho', 'hai', 'ha', 'abbiamo', 'avete', 'hanno'], essere: ['sono', 'sei', 'è', 'siamo', 'siete', 'sono'] },
  imp: { avere: ['avevo', 'avevi', 'aveva', 'avevamo', 'avevate', 'avevano'], essere: ['ero', 'eri', 'era', 'eravamo', 'eravate', 'erano'] },
  fut: { avere: ['avrò', 'avrai', 'avrà', 'avremo', 'avrete', 'avranno'], essere: ['sarò', 'sarai', 'sarà', 'saremo', 'sarete', 'saranno'] },
  cond: { avere: ['avrei', 'avresti', 'avrebbe', 'avremmo', 'avreste', 'avrebbero'], essere: ['sarei', 'saresti', 'sarebbe', 'saremmo', 'sareste', 'sarebbero'] },
  subj: { avere: ['abbia', 'abbia', 'abbia', 'abbiamo', 'abbiate', 'abbiano'], essere: ['sia', 'sia', 'sia', 'siamo', 'siate', 'siano'] },
  subjImp: { avere: ['avessi', 'avessi', 'avesse', 'avessimo', 'aveste', 'avessero'], essere: ['fossi', 'fossi', 'fosse', 'fossimo', 'foste', 'fossero'] },
  remoto: { avere: ['ebbi', 'avesti', 'ebbe', 'avemmo', 'aveste', 'ebbero'], essere: ['fui', 'fosti', 'fu', 'fummo', 'foste', 'furono'] },
};
const comp = (t: keyof typeof AUX) => (v: string, i: number) =>
  compound(AUX[t][ESSERE.has(v) ? 'essere' : 'avere'][i], agreeing(v, i));

const IMPERF = ['vo', 'vi', 'va', 'vamo', 'vate', 'vano'];
const THEME = { are: 'a', ere: 'e', ire: 'i' };
const FUT = ['ò', 'ai', 'à', 'emo', 'ete', 'anno'];
const COND = ['ei', 'esti', 'ebbe', 'emmo', 'este', 'ebbero'];
const CONG = { are: ['i', 'i', 'i', 'iamo', 'iate', 'ino'], other: ['a', 'a', 'a', 'iamo', 'iate', 'ano'] };
const CONG_IMP = ['ssi', 'ssi', 'sse', 'ssimo', 'ste', 'ssero'];
const REM = {
  are: [['ai'], ['asti'], ['ò'], ['ammo'], ['aste'], ['arono']],
  ere: [['ei', 'etti'], ['esti'], ['é', 'ette'], ['emmo'], ['este'], ['erono', 'ettero']],
  ire: [['ii'], ['isti'], ['ì'], ['immo'], ['iste'], ['irono']],
};

/** Congiuntivo presente with the present's spelling rules (cerchi, mangino). */
function congiuntivo(v: string, i: number): string {
  let r = root(v);
  let e = (type(v) === 'are' ? CONG.are : CONG.other)[i];
  if (type(v) === 'are') {
    if (/[cg]$/.test(r)) r += 'h';
    else if (r.endsWith('i')) e = e.slice(1);
  }
  return r + e;
}

const che = (_i: number, _a: string, who: string) => `che ${who}`;

function imperative(v: string, i: number): string[] | null {
  if (i === 1) return [type(v) === 'are' ? root(v) + 'a' : root(v) + 'i'];   // parla, prendi, dormi
  if (i === 2) return [congiuntivo(v, 2)];                                    // (Lei) parli
  if (i === 3) return [itPresent(v, 3)];
  if (i === 4) return [itPresent(v, 4)];
  return null;
}

const TENSES: Tense[] = [
  { id: 'imperfetto', title: 'Imperfetto', blurb: 'parlavo, prendevi, dormivamo…', verbs: ALL,
    forms: (v, i) => [root(v) + THEME[type(v)] + IMPERF[i]] },
  { id: 'futuro', title: 'Futuro semplice', blurb: 'parlerò, cercherai, dormiranno…', verbs: NOT_VEDERE,
    forms: (v, i) => [futStem(v) + FUT[i]] },
  { id: 'condizionale', title: 'Condizionale presente', blurb: 'parlerei, prenderesti…', verbs: NOT_VEDERE,
    forms: (v, i) => [futStem(v) + COND[i]] },
  { id: 'passato_prossimo', title: 'Passato prossimo', blurb: 'ho parlato, sono partita…', verbs: REG_PP, forms: comp('pres') },
  { id: 'trapassato_prossimo', title: 'Trapassato prossimo', blurb: 'avevo parlato, eravamo arrivati…', verbs: REG_PP, forms: comp('imp') },
  { id: 'futuro_anteriore', title: 'Futuro anteriore', blurb: 'avrò dormito, sarai tornato…', verbs: REG_PP, forms: comp('fut') },
  { id: 'condizionale_passato', title: 'Condizionale passato', blurb: 'avrei venduto…', verbs: REG_PP, forms: comp('cond') },
  { id: 'congiuntivo', title: 'Congiuntivo presente', blurb: 'che io parli, che loro dormano…', verbs: ALL,
    forms: (v, i) => [congiuntivo(v, i)], who: che },
  { id: 'congiuntivo_imperfetto', title: 'Congiuntivo imperfetto', blurb: 'che parlassi, che dormissero…', verbs: ALL,
    forms: (v, i) => [root(v) + THEME[type(v)] + CONG_IMP[i]], who: che },
  { id: 'congiuntivo_passato', title: 'Congiuntivo passato', blurb: 'che abbia parlato…', verbs: REG_PP, forms: comp('subj'), who: che },
  { id: 'congiuntivo_trapassato', title: 'Congiuntivo trapassato', blurb: 'che avessi parlato…', verbs: REG_PP, forms: comp('subjImp'), who: che },
  { id: 'passato_remoto', title: 'Passato remoto', blurb: 'parlai, dormisti, credette…', verbs: REMOTO,
    forms: (v, i) => REM[type(v)][i].map(e => root(v) + e) },
  { id: 'trapassato_remoto', title: 'Trapassato remoto (literary)', blurb: 'ebbi parlato, fui partito…', verbs: REG_PP, forms: comp('remoto') },
  { id: 'imperativo', title: 'Imperativo', blurb: 'parla!, prenda!, dormiamo!', verbs: ALL,
    people: [1, 2, 3, 4], forms: imperative, who: i => ['', '(tu)', '(Lei)', '(noi)', '(voi)'][i] },
];

export const IT_TENSES: LanguageTenses = {
  lang: 'it',
  people: [['io'], ['tu'], ['lui', 'lei'], ['noi'], ['voi'], ['loro']],
  tenses: TENSES,
};
